import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '@examdojo/core/widget';
import type {
  AxisConfig,
  AxisOrientation,
  BoundingBoxConfig,
  CoordinateSystemConfig,
  GridType,
  TickLabelFormat,
  TicksConfig,
  WidgetControl,
  WidgetSliderControl,
  WidgetSliderVariant,
  WidgetButtonControl,
} from '@examdojo/core/widget';
import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type {
  ElementDef,
  ElementDisplayConfig,
  FeatureElementDef,
  FeatureType,
  FunctionElementDef,
  FunctionType,
  GraphpadDisplayConfig,
  GraphpadParams,
  PrimitiveElementDef,
  PrimitiveType,
  ValueExpression,
} from '../../graphpad.model';
import { isFeatureElementDef, isFunctionElementDef, isPrimitiveElementDef } from '../../graphpad.model';
import {
  getFeatureDefaultParams,
  getFeatureTypes,
  getFunctionMeta,
  getFunctionTypes,
  getPrimitiveDefaultParams,
  getPrimitiveTypes,
} from '../graphpad-editor.model';
import { ElementEditorComponent } from './element-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

interface GridTypeOption {
  label: string;
  value: GridType;
}

interface FixedAxisOption {
  label: string;
  value: 'none' | 'x' | 'y';
}

type AxesMode = 'off' | 'default' | 'custom';
type EntryCategory = 'function' | 'feature' | 'primitive' | 'control';

interface FlatEntry {
  trackId: string;
  id: string;
  displayName: string;
  badge: string;
  category: EntryCategory;
  def?: ElementDef;
  display?: ElementDisplayConfig;
  isFunction?: boolean;
  isFeature?: boolean;
  isPrimitive?: boolean;
  control?: WidgetControl;
  controlIndex?: number;
}

@Component({
  selector: 'dojo-element-list-section',
  standalone: true,
  imports: [
    MatExpansionModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    ElementEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './element-list-section.component.html',
})
export class ElementListSectionComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.coordSystem();
      this.display();
      this.behaviorControls();
      this.syncCoordSystemFromConfig();
      this.syncControlsFromConfig();
    });

    this.xStartCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.xEndCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.yStartCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.yEndCtrl.valueChanges
      .pipe(
        tap(() => this.emitBoundingBoxChange()),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.ticksDistanceCtrl.valueChanges
      .pipe(
        tap((val) => this.emitTicksChange({ ticksDistance: val ?? undefined })),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.minorTicksCtrl.valueChanges
      .pipe(
        tap((val) => this.emitTicksChange({ minorTicks: val })),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.newEntryTypeCtrl.valueChanges
      .pipe(
        tap((val) => this.selectedAddType.set(val)),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.defaultPrecisionCtrl.valueChanges
      .pipe(
        tap((val) => {
          const currentDisplay = this.display() ?? {};
          this.displayChange.emit({
            ...currentDisplay,
            math: { ...currentDisplay.math, defaultPrecision: val ?? undefined },
          });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // --- Inputs ---
  readonly params = input.required<GraphpadParams>();
  readonly display = input<GraphpadDisplayConfig>();
  readonly coordSystem = input<CoordinateSystemConfig | undefined>();
  readonly behaviorControls = input<WidgetControl[]>([]);

  // --- Outputs ---
  readonly paramsChange = output<GraphpadParams>();
  readonly displayChange = output<GraphpadDisplayConfig>();
  readonly coordSystemChange = output<CoordinateSystemConfig>();
  readonly behaviorControlsChange = output<WidgetControl[]>();

  // --- Add form controls ---
  readonly newEntryTypeCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly selectedAddType = signal('');

  readonly defaultNewId = computed(() => {
    const raw = this.selectedAddType();
    if (!raw) {
      return '';
    }
    const [category, type] = raw.split(':');
    if (category === 'ctrl') {
      return `ctrl-${type}`;
    }
    const existingIds = Object.keys(this.params());
    return this.generateId(type, existingIds);
  });

  // --- Coord system controls ---
  readonly xStartCtrl = new FormControl<number>(-10, { nonNullable: true });
  readonly xEndCtrl = new FormControl<number>(10, { nonNullable: true });
  readonly yStartCtrl = new FormControl<number>(-10, { nonNullable: true });
  readonly yEndCtrl = new FormControl<number>(10, { nonNullable: true });
  readonly gridTypeCtrl = new FormControl<GridType>('lines', { nonNullable: true });
  readonly fixedAxisCtrl = new FormControl<'none' | 'x' | 'y'>('none', { nonNullable: true });
  readonly bgColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly borderColorCtrl = new FormControl<string>('', { nonNullable: true });
  readonly axesModeCtrl = new FormControl<AxesMode>('default', { nonNullable: true });
  readonly defaultPrecisionCtrl = new FormControl<number | null>(null);
  readonly ticksVisibleCtrl = new FormControl<boolean>(true, { nonNullable: true });
  readonly ticksDistanceCtrl = new FormControl<number | null>(null);
  readonly drawLabelsCtrl = new FormControl<boolean>(true, { nonNullable: true });
  readonly drawZeroCtrl = new FormControl<boolean>(false, { nonNullable: true });
  readonly minorTicksCtrl = new FormControl<number>(0, { nonNullable: true });
  readonly newEntryIdCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelFormatCtrl = new FormControl<string>('default', { nonNullable: true });

  // --- Per-axis controls (lazy) ---
  private readonly axisSelectCtrls = new Map<string, FormControl<string>>();
  private readonly axisNumberCtrls = new Map<string, FormControl<number | null>>();
  private readonly axisTextCtrls = new Map<string, FormControl<string>>();

  // --- Control editing form controls (lazy, keyed by controlId-field) ---
  private readonly ctrlTextCtrls = new Map<string, FormControl<string>>();
  private readonly ctrlNumberCtrls = new Map<string, FormControl<number | null>>();
  private readonly ctrlSelectCtrls = new Map<string, FormControl<string>>();

  // --- Add options (unified) ---
  readonly allAddOptions: SelectOption[] = [
    ...getFunctionTypes().map((t) => ({ label: `${t} · function`, value: `fn:${t}` })),
    ...getFeatureTypes().map((t) => ({ label: `${t} · feature`, value: `ft:${t}` })),
    ...getPrimitiveTypes().map((t) => ({ label: `${t} · primitive`, value: `pr:${t}` })),
    { label: 'slider · control', value: 'ctrl:slider' },
    { label: 'button · control', value: 'ctrl:button' },
  ];

  // --- Coord system options ---
  readonly gridTypeOptions: GridTypeOption[] = [
    { label: 'Lines', value: 'lines' },
    { label: 'Dots', value: 'dots' },
  ];

  readonly fixedAxisOptions: FixedAxisOption[] = [
    { label: 'Free', value: 'none' },
    { label: 'Fixed X', value: 'x' },
    { label: 'Fixed Y', value: 'y' },
  ];

  readonly bgColorOptions: SelectOption[] = THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c }));

  readonly borderColorOptions: SelectOption[] = [
    { label: 'Default (neutral-200)', value: 'neutral-200' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly labelFormatOptions: SelectOption[] = [
    { label: 'Default', value: 'default' },
    { label: 'Pi (\u03C0)', value: 'pi' },
    { label: 'Degrees (\u00B0)', value: 'degrees' },
  ];

  readonly axesModeOptions: SelectOption[] = [
    { label: 'Off', value: 'off' },
    { label: 'Default (on)', value: 'default' },
    { label: 'Custom', value: 'custom' },
  ];

  readonly axisOrientationOptions: SelectOption[] = [
    { label: 'Horizontal', value: 'horizontal' },
    { label: 'Vertical', value: 'vertical' },
  ];

  // --- Control editor options ---
  readonly controlPositionOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Top Left', value: 'top-left' },
    { label: 'Top', value: 'top' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Bottom Left', value: 'bottom-left' },
    { label: 'Bottom', value: 'bottom' },
    { label: 'Bottom Right', value: 'bottom-right' },
    { label: 'Slider Footer', value: 'slider-footer' },
  ];

  readonly controlColorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly controlVariantOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Minimal', value: 'minimal' },
  ];

  // --- Computed signals ---

  readonly flatEntries = computed<FlatEntry[]>(() => {
    const params = this.params();
    const displayConfig = this.display();
    const controls = this.behaviorControls();

    const entries: FlatEntry[] = [];

    for (const [id, def] of Object.entries(params)) {
      const fn = isFunctionElementDef(def);
      const ft = isFeatureElementDef(def);
      const pr = isPrimitiveElementDef(def);

      entries.push({
        trackId: `el-${id}`,
        id,
        displayName: id,
        badge: def.type,
        category: fn ? 'function' : ft ? 'feature' : 'primitive',
        def,
        display: displayConfig?.elements?.[id],
        isFunction: fn,
        isFeature: ft,
        isPrimitive: pr,
      });
    }

    for (let i = 0; i < controls.length; i++) {
      const ctrl = controls[i];
      entries.push({
        trackId: `ctrl-${ctrl.id}`,
        id: ctrl.id,
        displayName: ctrl.label || ctrl.id,
        badge: ctrl.type,
        category: 'control',
        control: ctrl,
        controlIndex: i,
      });
    }

    const order: Record<EntryCategory, number> = { function: 0, feature: 1, primitive: 2, control: 3 };
    entries.sort((a, b) => order[a.category] - order[b.category]);

    return entries;
  });

  readonly functionElementIds = computed<string[]>(() => {
    const params = this.params();
    return Object.entries(params)
      .filter(([, def]) => isFunctionElementDef(def))
      .map(([id]) => id);
  });

  readonly allElementIds = computed<string[]>(() => Object.keys(this.params()));

  readonly gridVisible = computed(() => {
    return this.coordSystem()?.grid?.visible !== false;
  });

  readonly fixedAxis = computed<'none' | 'x' | 'y'>(() => {
    return this.coordSystem()?.fixedAxis ?? 'none';
  });

  readonly axesMode = computed<AxesMode>(() => {
    const axes = this.coordSystem()?.axes;
    if (!axes) {
      return 'default';
    }
    if (axes.visible === false) {
      return 'off';
    }
    if (axes.custom) {
      return 'custom';
    }
    return 'default';
  });

  readonly axesArray = computed<AxisConfig[]>(() => {
    return this.coordSystem()?.axes?.custom ?? [];
  });

  readonly ticksVisible = computed(() => {
    return this.coordSystem()?.ticks?.visible !== false;
  });

  readonly borderVisible = computed(() => {
    return this.display()?.canvas?.border?.visible !== false;
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Coord System (public methods)
  // ═══════════════════════════════════════════════════════════════════════════

  getAxisCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.axisSelectCtrls.has(key)) {
      this.axisSelectCtrls.set(key, new FormControl<string>('', { nonNullable: true }));
    }
    return this.axisSelectCtrls.get(key)!;
  }

  getAxisNumberCtrl(idx: number, field: string): FormControl<number | null> {
    const key = `${idx}-${field}`;
    if (!this.axisNumberCtrls.has(key)) {
      const ctrl = new FormControl<number | null>(null);
      this.axisNumberCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            if (field === 'position') {
              this.onAxisFieldChange(idx, 'position', val ?? 0);
            } else if (field.startsWith('ticks.')) {
              this.onAxisTicksFieldChange(idx, field.split('.')[1], val ?? undefined);
            } else if (field.startsWith('axisLabel.')) {
              this.onAxisLabelFieldChange(idx, field.split('.')[1], val ?? undefined);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.axisNumberCtrls.get(key)!;
  }

  getAxisTextCtrl(idx: number, field: string): FormControl<string> {
    const key = `${idx}-${field}`;
    if (!this.axisTextCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.axisTextCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            if (field.startsWith('axisLabel.')) {
              this.onAxisLabelFieldChange(idx, field.split('.')[1], val || undefined);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.axisTextCtrls.get(key)!;
  }

  onGridVisibleChange(visible: boolean): void {
    const cs = this.coordSystem() ?? {};
    const currentGrid = cs.grid ?? {};
    this.coordSystemChange.emit({ ...cs, grid: { ...currentGrid, visible } });
  }

  onGridTypeChange(type: unknown): void {
    const cs = this.coordSystem() ?? {};
    const currentGrid = cs.grid ?? {};
    this.coordSystemChange.emit({ ...cs, grid: { ...currentGrid, type: type as GridType } });
  }

  onFixedAxisChange(value: unknown): void {
    const cs = this.coordSystem() ?? {};
    const v = value as 'none' | 'x' | 'y';
    this.coordSystemChange.emit({ ...cs, fixedAxis: v === 'none' ? undefined : v });
  }

  onAxesModeChange(mode: unknown): void {
    const { axes: _axes, ...cs } = this.coordSystem() ?? {};
    const m = mode as AxesMode;
    if (m === 'off') {
      this.coordSystemChange.emit({ ...cs, axes: { visible: false } });
    } else if (m === 'default') {
      // Omit axes key entirely → creator uses defaults
      this.coordSystemChange.emit(cs);
    } else {
      const defaultAxes: AxisConfig[] = [
        { id: 'x', orientation: 'horizontal', position: 0, isPrimary: true },
        { id: 'y', orientation: 'vertical', position: 0, isPrimary: true },
      ];
      this.coordSystemChange.emit({ ...cs, axes: { custom: defaultAxes } });
    }
  }

  onAxisFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    axes[idx] = { ...axes[idx], [field]: value };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onAxisTicksFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    axes[idx] = { ...axes[idx], ticks: { ...(axes[idx].ticks ?? {}), [field]: value } };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onAxisLabelFieldChange(idx: number, field: string, value: unknown): void {
    const axes = [...this.axesArray()];
    if (idx >= axes.length) {
      return;
    }
    const existing = axes[idx].axisLabel ?? { text: '' };
    axes[idx] = { ...axes[idx], axisLabel: { ...existing, [field]: value } };
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onDeleteAxis(idx: number): void {
    const axes = [...this.axesArray()];
    axes.splice(idx, 1);
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: axes.length ? { custom: axes } : { visible: false } });
  }

  onAddAxis(): void {
    const axes = [...this.axesArray()];
    const existingIds = axes.map((a) => a.id);
    let id = 'a';
    let counter = 1;
    while (existingIds.includes(id)) {
      id = `a${++counter}`;
    }
    const orientation: AxisOrientation = axes.length % 2 === 0 ? 'horizontal' : 'vertical';
    axes.push({ id, orientation, position: 0 });
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, axes: { custom: axes } });
  }

  onBgColorChange(color: unknown): void {
    const raw = (color as string) || undefined;
    const resolved = raw && !raw.includes('-') ? `${raw}-50` : raw;
    const currentDisplay = this.display() ?? {};
    const currentCanvas = currentDisplay.canvas ?? {};
    this.displayChange.emit({
      ...currentDisplay,
      canvas: { ...currentCanvas, color: resolved as typeof currentCanvas.color },
    });
  }

  onBorderVisibleChange(visible: boolean): void {
    const currentDisplay = this.display() ?? {};
    const currentCanvas = currentDisplay.canvas ?? {};
    const currentBorder = currentCanvas.border ?? {};
    this.displayChange.emit({
      ...currentDisplay,
      canvas: { ...currentCanvas, border: { ...currentBorder, visible } },
    });
  }

  onBorderColorChange(color: unknown): void {
    const raw = (color as string) || undefined;
    const resolved = raw && !raw.includes('-') ? `${raw}-200` : raw;
    const currentDisplay = this.display() ?? {};
    const currentCanvas = currentDisplay.canvas ?? {};
    const currentBorder = currentCanvas.border ?? {};
    this.displayChange.emit({
      ...currentDisplay,
      canvas: { ...currentCanvas, border: { ...currentBorder, color: resolved as typeof currentBorder.color } },
    });
  }

  onTicksVisibleChange(visible: boolean): void {
    this.emitTicksChange({ visible });
  }

  onDrawLabelsChange(enabled: boolean): void {
    this.emitTicksChange({ drawLabels: enabled });
  }

  onDrawZeroChange(enabled: boolean): void {
    this.emitTicksChange({ drawZero: enabled });
  }

  onLabelFormatChange(format: unknown): void {
    this.emitTicksChange({ labelFormat: (format as TickLabelFormat) || undefined });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Element handling
  // ═══════════════════════════════════════════════════════════════════════════

  onElementParamsChange(elementId: string, entry: FlatEntry, partialParams: Record<string, unknown>): void {
    const currentParams = this.params();
    const currentDef = currentParams[elementId];

    if (entry.isPrimitive) {
      const primDef = currentDef as PrimitiveElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...primDef,
          params: { ...primDef.params, ...partialParams },
        },
      });
    } else if (entry.isFunction) {
      const fnDef = currentDef as FunctionElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...fnDef,
          params: { ...(fnDef.params ?? {}), ...partialParams } as Record<string, ValueExpression>,
        },
      });
    } else {
      const ftDef = currentDef as FeatureElementDef;
      this.paramsChange.emit({
        ...currentParams,
        [elementId]: {
          ...ftDef,
          params: { ...(ftDef.params ?? {}), ...partialParams },
        },
      });
    }
  }

  onElementDisplayChange(elementId: string, newDisplay: ElementDisplayConfig): void {
    const currentDisplay = this.display() ?? {};
    this.displayChange.emit({
      ...currentDisplay,
      elements: {
        ...(currentDisplay.elements ?? {}),
        [elementId]: newDisplay,
      },
    });
  }

  onDeleteElement(elementId: string): void {
    const currentParams = { ...this.params() };
    delete currentParams[elementId];
    this.paramsChange.emit(currentParams);

    const currentDisplay = this.display() ?? {};
    if (currentDisplay.elements?.[elementId]) {
      const elements = { ...currentDisplay.elements };
      delete elements[elementId];
      this.displayChange.emit({ ...currentDisplay, elements });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Control handling (inlined from behavior-section)
  // ═══════════════════════════════════════════════════════════════════════════

  getCtrlTextCtrl(controlId: string, field: string): FormControl<string> {
    const key = `${controlId}-${field}`;
    if (!this.ctrlTextCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.ctrlTextCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            const idx = this.behaviorControls().findIndex((c) => c.id === controlId);
            if (idx >= 0) {
              this.onControlFieldChange(idx, field, val);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.ctrlTextCtrls.get(key)!;
  }

  getCtrlNumberCtrl(controlId: string, field: string): FormControl<number | null> {
    const key = `${controlId}-${field}`;
    if (!this.ctrlNumberCtrls.has(key)) {
      const ctrl = new FormControl<number | null>(null);
      this.ctrlNumberCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => {
            const idx = this.behaviorControls().findIndex((c) => c.id === controlId);
            if (idx >= 0) {
              this.onControlFieldChange(idx, field, val);
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.ctrlNumberCtrls.get(key)!;
  }

  getCtrlSelectCtrl(controlId: string, field: string): FormControl<string> {
    const key = `${controlId}-${field}`;
    if (!this.ctrlSelectCtrls.has(key)) {
      this.ctrlSelectCtrls.set(key, new FormControl<string>('', { nonNullable: true }));
    }
    return this.ctrlSelectCtrls.get(key)!;
  }

  isSliderEditable(control: WidgetControl): boolean {
    return control.type === WIDGET_CONTROL_TYPE.Slider && (control as WidgetSliderControl).editable === true;
  }

  isButtonShowLabel(control: WidgetControl): boolean {
    return control.type === WIDGET_CONTROL_TYPE.Button && (control as WidgetButtonControl).showLabel !== false;
  }

  onControlFieldChange(idx: number, field: string, value: unknown): void {
    const controls = [...this.behaviorControls()];
    if (idx < 0 || idx >= controls.length) {
      return;
    }

    const control = { ...controls[idx] } as Record<string, unknown>;

    if (field === 'rangeMin' || field === 'rangeMax' || field === 'rangeStep') {
      const slider = control as unknown as WidgetSliderControl;
      const range: [number, number, number?] = [...(slider.range ?? [0, 10])];
      if (field === 'rangeMin') {
        range[0] = (value as number) ?? 0;
      }
      if (field === 'rangeMax') {
        range[1] = (value as number) ?? 10;
      }
      if (field === 'rangeStep') {
        range[2] = (value as number) ?? undefined;
      }
      control['range'] = range;
    } else if (field === 'position') {
      control[field] = (value as string) || undefined;
    } else if (field === 'color') {
      control[field] = (value as string) || undefined;
    } else if (field === 'variant') {
      const variant = ((value as string) || undefined) as WidgetSliderVariant | undefined;
      control[field] = variant;
      // Apply variant to all slider controls for consistency
      for (let i = 0; i < controls.length; i++) {
        if (i !== idx && controls[i].type === WIDGET_CONTROL_TYPE.Slider) {
          controls[i] = { ...controls[i], variant } as unknown as WidgetControl;
        }
      }
    } else {
      control[field] = value;
    }

    controls[idx] = control as unknown as WidgetControl;
    this.behaviorControlsChange.emit(controls);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Add + Delete (unified)
  // ═══════════════════════════════════════════════════════════════════════════

  onDeleteEntry(entry: FlatEntry): void {
    if (entry.category === 'control') {
      const controls = [...this.behaviorControls()];
      controls.splice(entry.controlIndex!, 1);
      this.behaviorControlsChange.emit(controls);
    } else {
      this.onDeleteElement(entry.id);
    }
  }

  onAddEntry(): void {
    const raw = this.newEntryTypeCtrl.value;
    if (!raw) {
      return;
    }

    const [category, type] = raw.split(':');
    const customId = this.newEntryIdCtrl.value.trim();

    if (category === 'fn') {
      const existingIds = Object.keys(this.params());
      const id = customId || this.generateId(type, existingIds);
      if (existingIds.includes(id)) {
        return;
      }
      const meta = getFunctionMeta(type);
      this.paramsChange.emit({
        ...this.params(),
        [id]: { type: type as FunctionType, params: meta ? { ...meta.defaultParams } : {} },
      });
    } else if (category === 'ft') {
      const existingIds = Object.keys(this.params());
      const id = customId || this.generateId(type, existingIds);
      if (existingIds.includes(id)) {
        return;
      }
      const firstFnId = this.functionElementIds()[0];
      const defaults = getFeatureDefaultParams(type);
      this.paramsChange.emit({
        ...this.params(),
        [id]: {
          type: type as FeatureType,
          params: { ...(firstFnId ? { of: firstFnId } : {}), ...defaults },
        },
      });
    } else if (category === 'pr') {
      const existingIds = Object.keys(this.params());
      const id = customId || this.generateId(type, existingIds);
      if (existingIds.includes(id)) {
        return;
      }
      const defaults = getPrimitiveDefaultParams(type as PrimitiveType);
      this.paramsChange.emit({
        ...this.params(),
        [id]: { type: type as PrimitiveType, params: defaults },
      });
    } else if (category === 'ctrl') {
      const controls = [...this.behaviorControls()];
      const id = customId || `ctrl-${Date.now()}`;
      if (type === 'slider') {
        controls.push({
          type: WIDGET_CONTROL_TYPE.Slider,
          id,
          label: 'New Slider',
          param: '',
          range: [-10, 10, 0.1],
        } as WidgetSliderControl);
      } else if (type === 'button') {
        controls.push({
          type: WIDGET_CONTROL_TYPE.Button,
          id,
          label: 'New Button',
          trigger: { action: 'TRANSFORM_VIEW' },
        } as WidgetButtonControl);
      }
      this.behaviorControlsChange.emit(controls);
    }

    this.newEntryTypeCtrl.setValue('', { emitEvent: false });
    this.newEntryIdCtrl.setValue('', { emitEvent: false });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Private methods
  // ═══════════════════════════════════════════════════════════════════════════

  private syncCoordSystemFromConfig(): void {
    const cs = this.coordSystem();
    const canvas = this.display()?.canvas;
    if (cs?.boundingbox) {
      this.xStartCtrl.setValue(cs.boundingbox.xStart, { emitEvent: false });
      this.xEndCtrl.setValue(cs.boundingbox.xEnd, { emitEvent: false });
      this.yStartCtrl.setValue(cs.boundingbox.yStart, { emitEvent: false });
      this.yEndCtrl.setValue(cs.boundingbox.yEnd, { emitEvent: false });
    }
    if (cs?.grid) {
      this.gridTypeCtrl.setValue(cs.grid.type ?? 'lines', { emitEvent: false });
    }
    this.fixedAxisCtrl.setValue(cs?.fixedAxis ?? 'none', { emitEvent: false });
    this.bgColorCtrl.setValue(canvas?.color ?? '', { emitEvent: false });
    this.borderColorCtrl.setValue(canvas?.border?.color ?? '', { emitEvent: false });

    this.axesModeCtrl.setValue(this.axesMode(), { emitEvent: false });
    this.syncAxesControls();

    const dp = this.display()?.math?.defaultPrecision;
    this.defaultPrecisionCtrl.setValue(dp ?? null, { emitEvent: false });

    this.ticksVisibleCtrl.setValue(cs?.ticks?.visible !== false, { emitEvent: false });
    this.ticksDistanceCtrl.setValue(cs?.ticks?.ticksDistance ?? null, { emitEvent: false });
    this.drawLabelsCtrl.setValue(cs?.ticks?.drawLabels !== false, { emitEvent: false });
    this.drawZeroCtrl.setValue(cs?.ticks?.drawZero === true, { emitEvent: false });
    this.minorTicksCtrl.setValue(cs?.ticks?.minorTicks ?? 0, { emitEvent: false });
    this.labelFormatCtrl.setValue(cs?.ticks?.labelFormat ?? 'default', { emitEvent: false });
  }

  private syncAxesControls(): void {
    const axes = this.axesArray();
    for (let idx = 0; idx < axes.length; idx++) {
      const axis = axes[idx];
      this.getAxisCtrl(idx, 'orientation').setValue(axis.orientation ?? 'horizontal', { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'position').setValue(axis.position ?? 0, { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'ticks.ticksDistance').setValue(axis.ticks?.ticksDistance ?? null, {
        emitEvent: false,
      });
      this.getAxisCtrl(idx, 'ticks.labelFormat').setValue(axis.ticks?.labelFormat ?? 'default', { emitEvent: false });
      this.getAxisTextCtrl(idx, 'axisLabel.text').setValue(axis.axisLabel?.text ?? '', { emitEvent: false });
      this.getAxisNumberCtrl(idx, 'axisLabel.fontSize').setValue(axis.axisLabel?.fontSize ?? null, {
        emitEvent: false,
      });
    }
  }

  private emitTicksChange(partial: Partial<TicksConfig>): void {
    const cs = this.coordSystem() ?? {};
    this.coordSystemChange.emit({ ...cs, ticks: { ...cs.ticks, ...partial } });
  }

  private emitBoundingBoxChange(): void {
    const cs = this.coordSystem() ?? {};
    const boundingbox: BoundingBoxConfig = {
      xStart: this.xStartCtrl.value,
      xEnd: this.xEndCtrl.value,
      yStart: this.yStartCtrl.value,
      yEnd: this.yEndCtrl.value,
    };
    this.coordSystemChange.emit({ ...cs, boundingbox });
  }

  private syncControlsFromConfig(): void {
    const controls = this.behaviorControls();
    for (const control of controls) {
      this.getCtrlTextCtrl(control.id, 'label').setValue(control.label ?? '', { emitEvent: false });
      this.getCtrlSelectCtrl(control.id, 'position').setValue((control.position as string) ?? '', {
        emitEvent: false,
      });

      if (control.type === WIDGET_CONTROL_TYPE.Slider) {
        const slider = control as WidgetSliderControl;
        this.getCtrlTextCtrl(control.id, 'param').setValue(slider.param ?? '', { emitEvent: false });
        this.getCtrlNumberCtrl(control.id, 'rangeMin').setValue(slider.range?.[0] ?? 0, { emitEvent: false });
        this.getCtrlNumberCtrl(control.id, 'rangeMax').setValue(slider.range?.[1] ?? 10, { emitEvent: false });
        this.getCtrlNumberCtrl(control.id, 'rangeStep').setValue(slider.range?.[2] ?? null, { emitEvent: false });
        this.getCtrlNumberCtrl(control.id, 'defaultValue').setValue(slider.defaultValue ?? null, { emitEvent: false });
        this.getCtrlSelectCtrl(control.id, 'color').setValue((slider.color as string) ?? '', { emitEvent: false });
        this.getCtrlSelectCtrl(control.id, 'variant').setValue((slider.variant as string) ?? '', { emitEvent: false });
      } else if (control.type === WIDGET_CONTROL_TYPE.Button) {
        const button = control as WidgetButtonControl;
        this.getCtrlTextCtrl(control.id, 'icon').setValue(button.icon ?? '', { emitEvent: false });
      }
    }
  }

  private generateId(type: string, existingIds: string[]): string {
    const baseId = type.charAt(0);
    let id = baseId;
    let counter = 2;
    while (existingIds.includes(id)) {
      id = `${baseId}${counter}`;
      counter++;
    }
    return id;
  }
}
